import { Box, CircularProgress } from "@mui/material"
import { useEffect, useState } from "react"
import {
    Board,
    ConfirmCommitmentStatusModal,
    CreateOpportunitiesModal,
    DeleteObjectiveModal,
    Drawer,
    Grid,
    Header,
    ImpactedClientsModal,
    MainContent,
    RecommendationDetailsModal,
    RelatedOpportunitiesModal,
    TabFilters,
    UserCard
} from "src/lib"
import { LateralChat, OverviewSection } from "src/parts"
import { CommitmentsSection } from "src/parts/CommitmentsSection"
import { OpportunitiesSection } from "src/parts/OpportunitiesSection"
import { getUserHistoryMessages } from "src/services/api/chat"
import { intl } from "src/internationalization/intl"
import { isManagerProfile } from "src/lib/utils/isManagerProfile"
import { useMessageStore } from "src/store/messageStore"
import { useModalStore } from "src/store/modalStore"
import { useUserStore } from "src/store/userStore"

const AdvisorDashboard = () => {
    const openModal = useModalStore((s: any) => s.open)
    const payload = useModalStore((s: any) => s.payload)
    const closeModal = useModalStore((s: any) => s.closeModal)
    const { user, tokens } = useUserStore();
    const isManager = isManagerProfile(user?.profile);
    const [managerTab, setManagerTab] = useState<"advisor" | "manager">("advisor");

    const managerTabFilters = [
        { id: "advisor", label: intl("panelTitle"), value: "advisor" },
        { id: "manager", label: intl("managerPanelTitle"), value: "manager" },
    ] as const;
    const selectedManagerTab =
        managerTabFilters.find((f) => f.id === managerTab) ?? managerTabFilters[0];

    const panelTitle = isManager ? selectedManagerTab.label : intl("panelTitle");
    const loadHistoryMessages = useMessageStore((s: any) => s.loadHistoryMessages);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    useEffect(() => {
        const loadHistory = async () => {
            if (tokens.backendToken && !historyLoaded) {
                try {
                    const historyData = await getUserHistoryMessages();
                    if (historyData.history && historyData.history.length > 0) {
                        loadHistoryMessages(historyData.history);
                    }
                    setHistoryLoaded(true);
                } catch (error) {
                    console.error("Erro ao carregar histórico de mensagens:", error);
                    // Não bloqueia a aplicação se houver erro ao carregar histórico
                    setHistoryLoaded(true);
                }
            }
        };

        loadHistory();
    }, [tokens.backendToken, historyLoaded, loadHistoryMessages]);



    // Adicionar um loading estilizado aqui, esse loading foi necessario para evitar chamadas precoces 
    // à API antes do token ser setado na store pelo hook useAuthSync
    if (!tokens.backendToken) {
        return (
            <Box
                sx={{
                    display: "flex",
                    height: "100vh",
                    width: "100vw",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress size={80} thickness={4} />
            </Box>
        );
    }
    return (
        <>
            <Drawer.Root>
                <LateralChat />
            </Drawer.Root>
            <MainContent>
                <Header
                    title={panelTitle}
                    userName={user?.name || ""}
                >
                    <UserCard
                        userName={user?.name || ""}
                        userPosition="Assessoria Sênior"
                        avatarName={user?.name || "U"}
                        data-testid="user-informations"
                    />
                </Header>
                <Board data-testid="board">
                    {isManager && (
                        <Grid sx={{ mb: 2 }} data-testid="manager-panel-tabs">
                            <TabFilters
                                filters={[...managerTabFilters]}
                                selectedFilter={selectedManagerTab}
                                onFilterChange={(f) => setManagerTab(f.id as "advisor" | "manager")}
                            />
                        </Grid>
                    )}
                    {!isManager && (
                        <>
                            <OverviewSection />
                            <OpportunitiesSection />
                            <CommitmentsSection rankingsReadOnly />
                        </>
                    )}
                    {isManager && (
                        <>
                            <Box sx={{ display: managerTab === "advisor" ? "block" : "none" }}>
                                <OverviewSection />
                                <OpportunitiesSection
                                    showMonitoringPanel={false}
                                    showCreateObjectiveButton={false}
                                />
                                <CommitmentsSection rankingsReadOnly />
                            </Box>
                            <Box sx={{ display: managerTab === "manager" ? "block" : "none" }}>
                                <CommitmentsSection rankingOnly />
                                <OpportunitiesSection showMonitoringPanel />
                            </Box>
                        </>
                    )}
                </Board>
            </MainContent>
            {openModal === "createOpportunitiesModal" && isManager && (
                <CreateOpportunitiesModal open={true} onClose={closeModal} />
            )}
            {payload && openModal === 'relatedOpportunitiesModal' && (
                <RelatedOpportunitiesModal
                    open={true}
                    client={payload}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'recommendationDetailsModal' && (
                <RecommendationDetailsModal
                    open={true}
                    recommendation={payload.recommendation}
                    client={payload}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'confirmCommitmentStatusModal' && (
                <ConfirmCommitmentStatusModal
                    open={true}
                    commitmentId={payload.commitmentId}
                    currentStatus={payload.currentStatus}
                    clientName={payload.clientName}
                    accountId={payload.accountId}
                    dueDate={payload.dueDate}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'impactedClientsModal' && (
                <ImpactedClientsModal
                    open={true}
                    impactedClients={payload.impactedClients}
                    opportunityTitle={payload.opportunityTitle}
                    recommendation={payload.recommendation}
                    addedAt={payload.addedAt}
                    dueDate={payload.dueDate}
                    impactedClientsCount={payload.impactedClientsCount}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'deleteObjectiveModal' && (
                <DeleteObjectiveModal
                    open={true}
                    objectiveId={payload.objectiveId}
                    objectiveTitle={payload.objectiveTitle}
                    onClose={closeModal}/>
            )}

        </>
    )
}

export { AdvisorDashboard }

